<template>
  <div
    class="app-navbar__spacer"
    :class="{
      'app-navbar__spacer--with-timer': $slots.timer,
      'app-navbar__spacer--logo-only': logoOnly,
    }"
  ></div>

  <div :class="classes">
    <div
      v-if="showStraplines && straplines.length"
      class="app-navbar__straplines"
    >
      <div
        v-for="strapline in straplines"
        :key="strapline.id"
        class="app-navbar__strapline"
        :style="getStraplineStyles(strapline)"
      >
        <MarqueeText
          class="app-navbar__strapline-content"
          :text="strapline.text"
          :timeout="1000"
          :repeat-times="0"
          :speed-factor="0.7"
          :align="strapline.alignment"
        />
      </div>
    </div>

    <div class="app-navbar__bar">
      <NuxtLink to="/" class="app-navbar__logo">
        <img
          :src="`/images/logos/${locale}.svg`"
          :alt="site.data?.name"
          class="app-logo hidden md:block"
        />
        <img
          :src="`/images/logos/mobile-${locale}.svg`"
          :alt="site.data?.name"
          class="app-logo md:hidden"
        />
      </NuxtLink>

      <template v-if="!logoOnly">
        <button
          class="app-navbar__search-button"
          aria-label="search"
          @click="modals.openSearchModal"
        >
          <IconSearch />
        </button>

        <AppMenuDesktop class="!hidden xl:!flex" />

        <NuxtLink
          v-if="cartCount"
          to="/cart"
          class="app-menu-desktop__cart-button"
        >
          <span>{{ cartCount }}</span>

          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.24256 3.57144L3.69284 1L0 1.00004L2.36468e-05 3.00004L2.50604 3.00001L3.69755 4.97708L5.6823 11.152V15L16.3442 15L16.9858 13L7.74859 13V12L17.2909 12L20 3.57147L5.24256 3.57144ZM7.47601 10L6.05257 5.57144L17.1932 5.57147L15.7697 10L7.47601 10Z"
              fill="#E03119"
            />
            <circle cx="7" cy="18" r="2" fill="#E03119" />
            <circle cx="15" cy="18" r="2" fill="#E03119" />
          </svg>
        </NuxtLink>

        <UiHamburger
          :open="menus.isMobileMenuOpen"
          class="xl:!hidden"
          @click="menus.toggleMobileMenu"
        />
      </template>
    </div>

    <AppRegionMobile
      v-if="hasCurrencySelector && !logoOnly"
      v-model="isRegionOpen"
    />

    <slot name="timer"></slot>
  </div>

  <template v-if="!logoOnly">
    <Transition name="fade">
      <AppMenuMobile
        v-show="menus.isMobileMenuOpen"
        class="xl:!hidden"
        @open-region="openRegion"
      />
    </Transition>
  </template>
</template>

<script setup lang="ts">
import UiHamburger from '@boats/ui/components/UiHamburger.vue'
import type { Strapline } from '~/schemas'

const { locale } = useI18n()

const modals = useModalsStore()
const menus = useMenusStore()
const cart = useCartStore()

const cartCount = computed(
  () => (cart.data?.items?.length ?? 0) + (cart.data?.addOns?.length ?? 0),
)

type Props = {
  logoOnly?: boolean
}

const props = defineProps<Props>()
const currency = useCurrencyStore()
// Only the fallback (Ticketed) site can enable multiple currencies in CMS.
const hasCurrencySelector = computed(() => (currency.list?.length ?? 0) > 1)
const isRegionOpen = ref(false)
const openRegion = () => {
  menus.closeMobileMenu()
  isRegionOpen.value = true
}

useHead(() => ({
  htmlAttrs: {
    'data-mobile-region':
      hasCurrencySelector.value && !props.logoOnly ? 'true' : undefined,
  },
  bodyAttrs: {
    class:
      menus.isMobileMenuOpen && !props.logoOnly ? 'app-mobile-menu-open' : '',
  },
}))

const site = useSiteStore()
const showStraplines = true

const straplines = computed(() => {
  if (!showStraplines) return []
  return site.straplines ?? []
})

const updateNavbarCssVars = () => {
  if (!import.meta.client) return
  document.documentElement.style.setProperty(
    '--navbar-straplines-count',
    String(straplines.value.length),
  )
}

const getStraplineStyles = (strapline: Strapline) => ({
  backgroundColor: strapline.backgroundColour,
  color: strapline.textColour,
  '--app-navbar-link-hover-colour': strapline.linkHoverColour,
})

const classes = computed(() => ({
  'app-navbar': true,
  'app-navbar--logo-only': props.logoOnly,
}))

onMounted(() => {
  updateNavbarCssVars()
})

onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.documentElement.style.setProperty('--navbar-straplines-count', '0')
})

watch(
  () => straplines.value.length,
  () => updateNavbarCssVars(),
)
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

@include media-breakpoint-down(lg) {
  body.app-mobile-menu-open {
    overflow: hidden;
    overscroll-behavior: none;
  }
}

:root {
  --navbar-height: 3.5rem;
  --navbar-strapline-height: 34px;
  --navbar-straplines-count: 0;
  --navbar-region-height: 0px;
  --navbar-offset-height: calc(
    var(--navbar-height) + var(--navbar-region-height) +
      (var(--navbar-straplines-count) * var(--navbar-strapline-height))
  );

  @include media-breakpoint-up(md) {
    --navbar-height: 5rem;
  }
}

:root[data-mobile-region='true'] {
  --navbar-region-height: 44px;

  @include media-breakpoint-up(xl) {
    --navbar-region-height: 0px;
  }
}

.app-navbar {
  touch-action: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 995;

  @include media-breakpoint-up(lg) {
    position: fixed;
  }
}

.app-navbar__spacer {
  height: var(--navbar-height);

  height: calc(
    var(--navbar-height) + var(--navbar-region-height) +
      (var(--navbar-straplines-count, 0) * var(--navbar-strapline-height))
  );

  &--logo-only {
    --navbar-region-height: 0px;
  }

  &--with-timer {
    --navbar-height: 5.375rem;

    @include media-breakpoint-up(md) {
      --navbar-height: 7.5rem;
    }
  }
}

.app-navbar__straplines {
  display: block;
}

.app-navbar__strapline {
  height: var(--navbar-strapline-height);
  padding: 3px 0.75rem;
  display: flex;
  align-items: center;
}

.app-navbar__strapline-content {
  width: 100%;
  min-height: 28px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: 0;

  p {
    margin: 0;
  }

  a {
    color: inherit;
    text-decoration: underline;
    @include transition('color');

    &:hover {
      color: var(--app-navbar-link-hover-colour);
    }
  }
}

.app-navbar__bar {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: 0.375rem 0.75rem 0.375rem 0.375rem;
  color: $white;
  background-color: $black;

  @include media-breakpoint-up(md) {
    padding: 0.625rem 0.75rem 0.625rem 0.625rem;
  }
}

.app-navbar__logo {
  flex-shrink: 0;
  display: inline-flex;
  height: 2.75rem;
  margin-right: 0.75rem;

  .app-logo {
    width: auto;
    height: 100%;
  }

  .app-navbar--logo-only & {
    margin-left: auto;
    margin-right: auto;
  }

  @include media-breakpoint-up(md) {
    height: 3.75rem;
  }
}

.app-navbar__search-button {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.75rem;
  height: 2.75rem;
  margin-right: 0.75rem;
  background-color: $red;
  border-radius: 0.125rem;
  @include transition('background-color');

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background-color: $tint-red-hover;
  }

  @include media-breakpoint-up(md) {
    width: 3.75rem;
    height: 3.75rem;

    svg {
      width: 1.625rem;
      height: 1.625rem;
    }
  }
}

.ui-hamburger {
  margin-left: auto;
}

.app-menu-desktop__cart-button {
  flex-shrink: 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.75rem;
  height: 2.75rem;
  background-color: $white;
  color: $red;
  border-radius: 0.125rem;
  margin-left: auto;

  span {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    font-family: $font-barlow;
    font-weight: 500;
    font-size: 0.625rem;
    line-height: 1;
    letter-spacing: 0.1em;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  & + .ui-hamburger {
    margin-left: 0.75rem;
  }

  @include media-breakpoint-up(xl) {
    margin-left: 1.25rem;
  }

  @include media-breakpoint-up(xxl) {
    margin-left: 2.5rem;
  }
}
</style>
